import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiArrowLeft } = FiIcons;

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-slate-900 px-8 py-10 text-center relative">
          <Link 
            to="/login" 
            className="absolute left-6 top-6 text-slate-400 hover:text-white flex items-center gap-2 text-sm font-medium transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} /> Back to Login
          </Link>
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <SafeIcon icon={FiShield} className="text-[#00E5FF] text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-slate-300">BIS Cyber Security Academy (Sentinel)</p>
          <p className="text-slate-400 text-sm mt-4">Last Updated: {new Date().toLocaleDateString()}</p>
        </div>

        {/* Content */}
        <div className="p-8 sm:p-12 text-slate-600 space-y-8 text-sm sm:text-base leading-relaxed">
          
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">1. Introduction</h2>
            <p>
              Welcome to the BIS Cyber Security Academy ("Sentinel"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our internal training and security awareness platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
            <p className="mb-3">We collect information that is necessary to provide, manage, and track your cybersecurity training and compliance. This includes:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-700">
              <li><strong>Personal Identification Information:</strong> Name, email address, job title, and department.</li>
              <li><strong>Training Data:</strong> Course progress, completion status, quiz scores, and acknowledgements of company policies.</li>
              <li><strong>Simulation Data:</strong> Interaction metrics with authorized internal phishing simulations (e.g., whether an email was opened, a link was clicked, or an incident was correctly reported).</li>
              <li><strong>System Usage Data:</strong> Login timestamps, IP addresses (for security monitoring), and platform interaction logs.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="mb-3">We use the collected information for the following internal corporate purposes:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-700">
              <li>To deliver assigned security awareness training modules.</li>
              <li>To track and report compliance with mandatory corporate security policies.</li>
              <li>To evaluate organizational security posture and identify areas requiring additional training.</li>
              <li>To generate completion certificates and compliance reports for management and HR.</li>
              <li>To send automated reminders regarding incomplete or overdue training assignments.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">4. Information Sharing and Disclosure</h2>
            <p>
              We <strong>do not</strong> sell, rent, or trade your personal information to third parties. Your data is strictly used for internal corporate governance and security purposes. Information may be shared internally with:
            </p>
            <ul className="list-disc pl-5 mt-3 space-y-2 text-slate-700">
              <li>Your direct managers and department heads (for compliance tracking).</li>
              <li>The IT Security and Human Resources departments (for auditing and policy enforcement).</li>
              <li>Authorized third-party service providers acting on our behalf (e.g., hosting providers), bound by strict confidentiality agreements.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">6. Your Responsibilities</h2>
            <p>
              As an employee using this platform, you are responsible for maintaining the confidentiality of your account credentials and for completing all assigned training modules and policy acknowledgements honestly and on time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">7. Contact Us</h2>
            <p>
              If you have questions or comments about this Privacy Policy or your training records, please contact the IT Security team at:
            </p>
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-100 inline-block">
              <p className="font-medium text-slate-900">Email: <a href="mailto:security@blessinnovativesolutions.com" className="text-blue-600 hover:underline">security@blessinnovativesolutions.com</a></p>
            </div>
          </section>

        </div>
        
        {/* Footer */}
        <div className="bg-slate-50 p-6 text-center border-t border-slate-200">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} Bless Innovative Solutions. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;